/**
 * Guests' avatars — the non-React half of the web `useGuestAvatars` hook.
 *
 * The engine keeps guests as bare usernames, because that is what the stop
 * payload accepts and what survives a reload. Their pictures are not session
 * data — they belong to the account and can be looked up — so they are
 * fetched here rather than frozen into the workout record.
 *
 * The lookup is the friend quick-search in its FULL projection, which is the
 * only shape carrying `avatar_url`. Results are cached for the life of the
 * session: a rotation redraws every tick, and every guest was picked from
 * this same list moments earlier.
 */

import { registerSessionScoped } from '../../../store/session-scope';

/** username → avatar URL, for whoever could be resolved. */
export type GuestAvatars = Record<string, string>;

/** The friend suggestion in its full projection — the fields read here. */
export interface FriendSuggestion {
  value: string;
  avatar_url?: string | null;
}

/** The search the lookup goes through — `selectizeService` on both platforms. */
export interface FriendSearch {
  search: (
    resource: 'friend',
    term: string,
    options: { compact: false }
  ) => Promise<unknown>;
}

/** Session-lifetime cache — the friend list barely changes inside one workout. */
export const guestAvatarCache: GuestAvatars = {};

/** Drop every remembered avatar. Exposed for tests, which share a module graph. */
export const clearGuestAvatarCache = (): void => {
  Object.keys(guestAvatarCache).forEach((username) => {
    delete guestAvatarCache[username];
  });
};

registerSessionScoped('sessionGuestAvatars', clearGuestAvatarCache);

/** Who still needs a picture. */
export const unresolvedGuests = (
  usernames: string[],
  cache: GuestAvatars = guestAvatarCache
): string[] => usernames.filter((username) => !cache[username]);

/**
 * Resolve the guests' avatars, fetching only when someone is still
 * unpictured. Returns a snapshot of the cache either way. A failed lookup is
 * swallowed: mid-workout, a missing picture falls back to the Dō mark and is
 * not worth an error.
 */
export const resolveGuestAvatars = async (
  usernames: string[],
  search: FriendSearch,
  cache: GuestAvatars = guestAvatarCache
): Promise<GuestAvatars> => {
  if (unresolvedGuests(usernames, cache).length === 0) return { ...cache };

  try {
    const suggestions = (await search.search('friend', '', {
      compact: false,
    })) as FriendSuggestion[];
    suggestions.forEach((friend) => {
      if (friend.avatar_url) cache[friend.value] = friend.avatar_url;
    });
  } catch {
    // leave the cache as it was
  }

  return { ...cache };
};
